import clsx from 'clsx';
import {
  forwardRef,
  useId,
  type ReactElement,
  type ReactNode,
  Children,
} from 'react';
import { useIntl } from 'react-intl';
import { isElement } from 'react-is';

import {
  _canReceiveDefaultTextFormat,
  _mergeProps,
  type DOMProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import {
  CriticalIcon,
  InformationIcon,
  SuccessIcon,
  WarningIcon,
} from '@dynatrace/strato-icons';

import { useInformationOverlayContext } from './hooks/useInformationOverlayContext.js';
import {
  InformationOverlayTriggerCSS,
  InformationOverlayTriggerIconCSS,
  InformationOverlayTriggerIconHoverCSS,
  InformationOverlayTriggerIconVariantsCSS,
  InformationOverlayTriggerTextCSS,
} from './InformationOverlay.sty.js';
import { InformationOverlayIcon } from './InformationOverlayIcon.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';

//#region Typings
/**
 * @public
 */
export interface InformationOverlayTriggerProps extends DOMProps, WithChildren {
  /** Optional aria-label to provide an additional description for the trigger. */
  'aria-label'?: string;
}

type TriggerSlots = {
  icon?: ReactElement;
  content?: ReactNode;
};
//#endregion Typings

/**
 * InformationOverlay Trigger component, used for rendering trigger content.
 * @public
 */
export const Trigger = /* @__PURE__ */ forwardRef<
  HTMLButtonElement,
  InformationOverlayTriggerProps
>((props, forwardedRef) => {
  const { children, 'aria-label': ariaLabelProp, id: propId } = props;
  const { color, overlayTriggerProps } = useInformationOverlayContext();
  const id = useId();

  const { focusProps, focusClassName, isFocusVisible } = useFocusRing({
    color: children ? 'neutral' : color,
    isMinimal: true,
  });

  const slots: TriggerSlots = {
    icon: undefined,
  };

  switch (color) {
    case 'success':
      slots.icon = <SuccessIcon />;
      break;
    case 'warning':
      slots.icon = <WarningIcon />;
      break;
    case 'critical':
      slots.icon = <CriticalIcon />;
      break;
    default:
      slots.icon = <InformationIcon />;
      break;
  }

  Children.forEach(children, (child: ReactNode) => {
    if (isElement(child) && child.type === InformationOverlayIcon) {
      slots.icon = child;
    } else {
      slots.content = child;
    }
  });

  // If the trigger gets string children, then there is
  // 1. no need for an aria label
  // 2. The aria label would need to at least contain the parts of the children string based on https://accessibilityinsights.io/info-examples/web/needs-review/label-content-name-mismatch/
  const intl = useIntl();
  let ariaLabel = ariaLabelProp;

  if (
    ariaLabel === undefined &&
    (children === undefined || !_canReceiveDefaultTextFormat(children))
  ) {
    ariaLabel = intl.formatMessage({
      defaultMessage: 'Open additional information.',
      id: 'NXdoElYQZNKea1Sc',
      description: 'Aria label fallback for Information overlay trigger',
    });
  }

  return (
    <button
      className={InformationOverlayTriggerCSS}
      ref={forwardedRef}
      aria-haspopup="dialog"
      aria-label={ariaLabel}
      id={propId ?? id}
      {..._mergeProps(overlayTriggerProps, focusProps)}
    >
      <InformationOverlayIcon
        className={clsx(
          InformationOverlayTriggerIconCSS,
          InformationOverlayTriggerIconVariantsCSS[color],
          {
            [focusClassName]: !slots.content,
            [InformationOverlayTriggerIconHoverCSS]: !slots.content,
          },
          { 'strato-focus-visible': isFocusVisible },
        )}
      >
        {slots.icon}
      </InformationOverlayIcon>
      {slots.content && (
        <span
          className={clsx(focusClassName, InformationOverlayTriggerTextCSS, {
            'strato-focus-visible': isFocusVisible,
          })}
          style={{
            flex: 1,
            overflow: 'hidden',
            textDecoration: isFocusVisible ? 'none' : undefined,
          }}
        >
          {slots.content}
        </span>
      )}
    </button>
  );
});

(Trigger as typeof Trigger & { displayName: string }).displayName =
  'InformationOverlay.Trigger';
